import React from "react";
import { createPortal } from "react-dom";
import { useTimeSettings, formatTime, formatDateLabel } from "../hooks/useTimeSettings";

// Group checks into hourly time slots and calculate uptime percentage
function groupIntoTimeSlots(rows, targetBars = 168, slotMinutes = 60) {
  if (!rows || rows.length === 0) return [];
  
  const slotMs = slotMinutes * 60 * 1000;
  const now = Date.now();
  
  // Align to hour boundaries: round down to the nearest hour
  const currentHour = new Date(now);
  currentHour.setMinutes(0, 0, 0);
  const currentHourMs = currentHour.getTime();
  
  // Create time slots aligned to hour boundaries, going backwards
  const slots = [];
  for (let i = 0; i < targetBars; i++) {
    let slotEnd;
    let slotStart;
    
    if (i === 0) {
      // Most recent slot: starts at current hour boundary, ends at "now"
      slotStart = currentHourMs;
      slotEnd = now;
    } else {
      // Past slots: aligned to hour boundaries
      slotEnd = currentHourMs - ((i - 1) * slotMs);
      slotStart = slotEnd - slotMs;
    }
    
    // Find all checks within this time slot
    const slotRows = rows.filter(r => {
      if (i === 0) {
        return r.ts >= slotStart && r.ts <= slotEnd;
      } else {
        return r.ts >= slotStart && r.ts < slotEnd;
      }
    });
    
    let uptimePct = null;
    let totalChecks = 0;
    let upChecks = 0;
    
    if (slotRows.length > 0) {
      totalChecks = slotRows.length;
      upChecks = slotRows.filter(r => r.ok === true).length;
      uptimePct = (upChecks / totalChecks) * 100;
    }
    
    slots.push({
      slotStart,
      slotEnd,
      slotRows,
      uptimePct,
      totalChecks,
      upChecks,
    });
  }
  
  // Reverse so most recent is on the right (oldest first, newest last)
  return slots.reverse();
}

// Get color based on uptime percentage
function getUptimeColor(uptimePct) {
  if (uptimePct === null) return "transparent";
  if (uptimePct >= 99) return "#10b981"; // green for 99%+
  if (uptimePct >= 95) return "#84cc16"; // light green for 95-99%
  if (uptimePct >= 90) return "#eab308"; // yellow for 90-95%
  if (uptimePct >= 50) return "#f97316"; // orange for 50-90%
  return "#ef4444"; // red for <50%
}

function formatTimeSlot(startTs, endTs, timeFormat, timezone) {
  const dateStr = formatDateLabel(startTs, timezone);
  const startTime = formatTime(startTs, timeFormat, timezone);
  const endTime = formatTime(endTs, timeFormat, timezone);
  
  return `${dateStr}, ${startTime} - ${endTime}`;
}

function ExpandedMonitorChart({ data }) {
  const width = 1200; // Wider for expanded view
  const height = 200; // Taller for expanded view
  const padding = 8;
  const targetBars = 168; // 7 days of hourly data (168 hours)
  const slotMinutes = 60; // Each bar represents 1 hour
  const [hoveredBar, setHoveredBar] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0, above: true });
  const chartRef = React.useRef(null);
  const { timeFormat, timezone } = useTimeSettings();

  const barHeight = height - padding * 2;
  
  const barData = React.useMemo(() => {
    const rows = data || [];
    if (!rows.length) return [];

    // Group checks into hourly time slots
    const timeSlots = groupIntoTimeSlots(rows, targetBars, slotMinutes);
    const barWidth = Math.max(2, (width - padding * 2) / targetBars - 1);

    // Create bars for each time slot
    const bars = timeSlots.map((slot, i) => {
      const x = padding + i * (barWidth + 1);
      const color = getUptimeColor(slot.uptimePct);

      return {
        x,
        width: barWidth,
        height: barHeight,
        color,
        data: slot,
        index: i,
      };
    });

    return bars;
  }, [data, barHeight, targetBars, slotMinutes, width, padding]);

  const handleMouseMove = React.useCallback((e) => {
    if (!chartRef.current) return;
    
    const svg = chartRef.current.querySelector('svg');
    if (!svg) return;
    
    // Calculate SVG coordinates accounting for viewBox scaling
    const svgRect = svg.getBoundingClientRect();
    const scaleX = width / svgRect.width;
    const scaleY = height / svgRect.height;
    const svgX = (e.clientX - svgRect.left) * scaleX;
    const svgY = (e.clientY - svgRect.top) * scaleY;
    
    // Find which bar is being hovered
    const hovered = barData.find(bar => {
      return svgX >= bar.x && svgX <= bar.x + bar.width && 
             svgY >= padding && svgY <= padding + bar.height;
    });
    
    if (hovered && hovered.data && hovered.data.uptimePct !== null) {
      setHoveredBar(hovered);
      
      // Calculate the bar's center position in viewport coordinates
      const scaleX = svgRect.width / width;
      const scaleY = svgRect.height / height;
      
      const barCenterXScreen = (hovered.x + hovered.width / 2) * scaleX;
      const barTopYScreen = padding * scaleY;
      const barBottomYScreen = (padding + barHeight) * scaleY;
      
      // Convert to viewport coordinates
      const barCenterXViewport = svgRect.left + barCenterXScreen;
      const barTopYViewport = svgRect.top + barTopYScreen;
      const barBottomYViewport = svgRect.top + barBottomYScreen;
      
      // Position tooltip above the bar, centered on it
      let tooltipX = barCenterXViewport;
      let tooltipY = barTopYViewport - 8;
      let above = true;
      
      // Adjust if tooltip would go off edges
      const tooltipHalfWidth = 110;
      if (tooltipX - tooltipHalfWidth < 10) {
        tooltipX = tooltipHalfWidth + 10;
      }
      
      if (tooltipX + tooltipHalfWidth > window.innerWidth - 10) {
        tooltipX = window.innerWidth - tooltipHalfWidth - 10;
      }
      
      if (tooltipY < 100) {
        tooltipY = barBottomYViewport + 8;
        above = false;
      }
      
      if (tooltipX < 0) tooltipX = 10;
      if (tooltipY < 0) tooltipY = 10;
      
      setTooltipPos({ x: tooltipX, y: tooltipY, above });
    } else {
      setHoveredBar(null);
    }
  }, [barData, width, height, barHeight, padding]);

  const handleMouseLeave = React.useCallback(() => {
    setHoveredBar(null);
  }, []);

  const tooltipData = React.useMemo(() => {
    if (!hoveredBar || !hoveredBar.data || hoveredBar.data.uptimePct === null) return null;
    
    const slot = hoveredBar.data;
    const timeSlotStr = formatTimeSlot(slot.slotStart, slot.slotEnd, timeFormat, timezone);
    const uptimePct = slot.uptimePct;
    const totalChecks = slot.totalChecks;
    const upChecks = slot.upChecks;
    const downChecks = totalChecks - upChecks;
    
    // Calculate average response time for this time slot
    const slotRows = slot.slotRows || [];
    const avgResponseTime = slotRows.length > 0 && slotRows.some(r => r.ms) 
      ? (slotRows.reduce((sum, r) => sum + (r.ms || 0), 0) / slotRows.filter(r => r.ms).length) / 1000
      : null;
    
    return {
      timeSlotStr,
      uptimePct: uptimePct.toFixed(2),
      totalChecks,
      upChecks,
      downChecks,
      avgResponseTime: avgResponseTime !== null ? avgResponseTime.toFixed(1) : null,
    };
  }, [hoveredBar, timeFormat, timezone]);

  return (
    <div 
      style={{ width: "100%", height: height, position: "relative" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      ref={chartRef}
    >
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%">
        <rect x="0" y="0" width={width} height={height} fill="transparent" />
        {barData.length > 0 ? (
          barData.map((bar, i) => (
            <rect
              key={i}
              x={bar.x}
              y={padding}
              width={bar.width}
              height={bar.height}
              fill={bar.color}
              rx="1"
              style={{ 
                cursor: bar.data && bar.data.uptimePct !== null ? "pointer" : "default",
                opacity: hoveredBar && hoveredBar.index === bar.index ? 0.8 : 1,
              }}
            />
          ))
        ) : (
          <text x="8" y={height / 2} fill="var(--muted)" fontSize="12">
            No data yet
          </text>
        )}
      </svg>
      
      {tooltipData && hoveredBar && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: "fixed",
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: tooltipPos.above ? "translate(-50%, -100%)" : "translate(-50%, 0)",
            background: "var(--panel)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "12px",
            fontSize: "12px",
            color: "var(--text)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
            pointerEvents: "none",
            minWidth: "200px",
            maxWidth: "250px",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "8px", color: getUptimeColor(parseFloat(tooltipData.uptimePct)) }}>
            {tooltipData.uptimePct}% Uptime
          </div>
          <div style={{ color: "var(--text-muted)", marginBottom: "4px" }}>
            {tooltipData.timeSlotStr}
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: "11px", marginBottom: "4px" }}>
            {tooltipData.totalChecks} check{tooltipData.totalChecks !== 1 ? 's' : ''} ({tooltipData.upChecks} up, {tooltipData.downChecks} down)
          </div>
          {tooltipData.avgResponseTime !== null && (
            <div style={{ color: "var(--text-muted)", fontSize: "11px" }}>
              Avg response: {tooltipData.avgResponseTime}s
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default React.memo(ExpandedMonitorChart);

